import { axiosInstance } from "./config";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { logout } from "../utils/processors";
import { useUser } from "../contexts/user-context";

const usersUrl = 'users/'

// Hook to get team members
export const useGetMe = (options = {}) => {
  return useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      return axiosInstance.get(usersUrl + 'auth/me/');
    },
    retry: false,
    ...options,
  });
};

export const useAuthenticate = (options = {}) => {
  return useQuery({
    queryKey: ['authenticate'],
    queryFn: async () => {
      return axiosInstance.post(usersUrl + 'auth/');
    },
    retry: false,
    ...options,
  });
};

export const useSetRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (role) => {
      return axiosInstance.post(usersUrl + 'auth/set_role/', {role:role});
    },
    onSuccess: () => {
      // Invalidate or refresh the relevant query after successful update
      queryClient.invalidateQueries(['users']);
    },
  });
};

export const useGetFullPortfolio = () => {
  // Retrieve profileId from local storage
  const { user } = useUser()
  const profileId = user?.profile.profile_id;
  return useQuery({
    queryKey: ['users', profileId],
    queryFn: async () => {
      if (!profileId) {
        throw new Error("Profile ID is missing in local storage.");
      }
      try {
        const response = await axiosInstance.get(usersUrl + `profiles/${profileId}/`);
        return response.data;
      } catch (error) {
        if (error.response?.status === 404) {
          logout(); // Clean localStorage and navigate to login if 404
        }
        throw error; 
      }
    },
    enabled: !!profileId, // Run the query only if profileId exists
    staleTime: 0,
    refetchOnMount: true,
  });
};

export function useParseResume() {
  return useMutation({
    mutationFn: async (payload) => {
      // Prepare FormData
      const formData = new FormData();
      formData.append('resume', payload); // 'resume' should match the expected field name on the backend
      // Send the request
      return axiosInstance.post(usersUrl + 'profiles/parse-resume/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data', // Required for file uploads
        },
      });
    },
  });
};

export function useReplaceResume() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('resume', file);
      const response = await axiosInstance.post(
        usersUrl + 'profiles/replace-resume/',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { user } = useUser()
  const profileId = user?.profile.profile_id; 
  
  return useMutation({
    mutationFn: async (data) => {
      if (!profileId) {
        throw new Error("Profile ID is missing");
      }
      return axiosInstance.patch(usersUrl + `profiles/${profileId}/`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}


export function useChangeTemplate() {
  const queryClient = useQueryClient();
  const { user } = useUser()
  const profileId = user?.profile?.profile_id;

  return useMutation({
    mutationFn: async (data) => {
      // Replace this URL with your actual experience API endpoint
      return axiosInstance.put(usersUrl+'profiles/change-template/', {...data,profile_id:profileId});
    },
    onSuccess: () => {
      // Force-refresh the concrete profile query used by GET users/profiles/{id}/.
      if (profileId) {
        queryClient.invalidateQueries({ queryKey: ['users', profileId], refetchType: 'all' });
      }
      queryClient.invalidateQueries({ queryKey: ['users'], refetchType: 'all' });
    },
  });
}

export function useUpdateOnboardingStep() {
  const queryClient = useQueryClient();
  const { user } = useUser();
  const profileId = user?.profile?.profile_id || user?.profile?.id;

  return useMutation({
    mutationFn: async (step) => {
      if (!profileId) {
        throw new Error("Profile ID is missing");
      }
      return axiosInstance.patch(usersUrl + `profiles/${profileId}/`, { onboarding_step: step });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['users', 'me']);
      queryClient.invalidateQueries(['portfolio']);
    },
  });
}

export const useGetFullPortfolioByName = (profileName) => {
  return useQuery({
    queryKey: ['users', 'profileName', profileName],
    queryFn: async () => {
      if (!profileName) {
        throw new Error("Profile name is missing.");
      }
      try {
        const response = await axiosInstance.get(
          usersUrl + `profiles/by-name/${profileName}/`
        );
        return response.data;
      } catch (error) {
        if (error.response?.status === 404) {
          logout(); // Same behavior as the profileId version
        }
        throw error;
      }
    },
    enabled: !!profileName, // Only run if profileName is provided
    staleTime: 0,
    refetchOnMount: true,
  });
};
